import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Skeleton } from "../components/ui/skeleton";
import { Badge } from "../components/ui/badge";
import { Switch } from "../components/ui/switch";
import { Label } from "../components/ui/label";
import { CashFlowForecast } from "../components/CashFlowForecast";
import { WhatIfSimulator } from "../components/WhatIfSimulator";
import { AISummary } from "../components/AISummary";
import {
  TrendingUp,
  ArrowLeft,
  Bell,
  AlertTriangle,
  CheckCircle2
} from "lucide-react";
import { toast } from "sonner";

export const CashFlowPage = () => {
  const navigate = useNavigate();
  const { api, user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [alertSettings, setAlertSettings] = useState({ enabled: true });
  const [savingSettings, setSavingSettings] = useState(false);
  const [forecast, setForecast] = useState(null);
  const [bills, setBills] = useState([]);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [settingsRes, forecastRes, billsRes] = await Promise.all([
        api.get("/user/cashflow-alert-settings"),
        api.get("/cash-flow/forecast?days=90"),
        api.get("/bills/upcoming")
      ]);
      setAlertSettings(settingsRes.data);
      setForecast(forecastRes.data);
      setBills(billsRes.data || []);
    } catch (error) {
      console.error("Failed to fetch data:", error);
    } finally {
      setLoading(false);
    }
  }, [api]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleToggleAlerts = async (enabled) => {
    setSavingSettings(true);
    try {
      await api.put("/user/cashflow-alert-settings", { enabled });
      setAlertSettings(prev => ({ ...prev, enabled }));
      toast.success(enabled ? "Cash flow alerts enabled" : "Cash flow alerts disabled");
    } catch (error) {
      toast.error("Failed to update settings");
    } finally {
      setSavingSettings(false);
    }
  };

  const formatCurrency = (amount) => {
    const currency = user?.currency || "CAD";
    return new Intl.NumberFormat("en-CA", {
      style: "currency",
      currency: currency,
    }).format(amount);
  };

  const formatDate = (dateStr) => {
    try {
      const date = new Date(dateStr);
      return date.toLocaleDateString("en-CA", { 
        weekday: "long", 
        year: "numeric", 
        month: "long", 
        day: "numeric" 
      });
    } catch {
      return dateStr;
    }
  };

  if (loading) {
    return (
      <div className="space-y-6" data-testid="cashflow-loading">
        <div className="flex items-center gap-4">
          <Skeleton className="h-10 w-10 rounded-full" />
          <div>
            <Skeleton className="h-8 w-48 mb-2" />
            <Skeleton className="h-5 w-64" />
          </div>
        </div>
        <Skeleton className="h-[400px] rounded-xl" />
      </div>
    );
  }

  const hasShortfall = forecast && forecast.has_shortfall;
  const shortfalls = forecast?.shortfalls || [];
  const firstShortfallDate = forecast?.first_shortfall_date;
  const firstShortfallDays = forecast?.first_shortfall_days;

  return (
    <div data-testid="cashflow-page" className="space-y-6">
      <div className="phead"><div><h1>Cash Flow Forecast</h1><div className="sub">A 90-day projection of your chequing balance</div></div></div>
      <AISummary page="cashflow" />

      {/* Alert Status */}
      <div className="card" style={{padding:"20px 24px",display:"flex",alignItems:"center",gap:16}}>
        <div className="ai2" style={{background: hasShortfall ? "rgba(176,96,63,.08)" : "#e4f0dd"}}>
          {hasShortfall ? <AlertTriangle size={22} stroke="#B0603F" /> : <CheckCircle2 size={22} stroke="#3F7D52" />}
        </div>
        <div style={{flex:1}}>
          <b style={{fontSize:16}}>{hasShortfall ? "Shortfall detected in " + firstShortfallDays + " days" : "No shortfalls detected"}</b>
          <div className="muted" style={{fontSize:14}}>{hasShortfall ? "You may need to move funds or reduce spending" : "Your balance stays healthy for the next 90 days."}</div>
        </div>
      </div>

      {/* Cash Flow Forecast Chart */}
      <CashFlowForecast />

      {/* What If Simulator */}
      <WhatIfSimulator originalForecast={forecast} bills={bills} />

      {/* Shortfall Details */}
      {shortfalls.length > 0 && (
        <Card className="border-rose-200 dark:border-rose-800">
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2 text-rose-900 dark:text-rose-100">
              <AlertTriangle className="h-5 w-5 text-rose-600" />
              Upcoming Shortfalls
            </CardTitle>
            <CardDescription>
              Days when your checking account is projected to go negative
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {shortfalls.slice(0, 5).map((shortfall, index) => (
                <ShortfallItem 
                  key={index} 
                  shortfall={shortfall} 
                  formatDate={formatDate}
                  formatCurrency={formatCurrency}
                />
              ))}
              {shortfalls.length > 5 && (
                <p className="text-sm text-muted-foreground text-center">
                  And {shortfalls.length - 5} more shortfall days...
                </p>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Email Alert Info */}
      <Card className="border-border/50 bg-muted/30">
        <CardContent className="p-4">
          <div className="flex items-start gap-3">
            <Bell className="h-5 w-5 text-muted-foreground flex-shrink-0 mt-0.5" />
            <div className="text-sm text-muted-foreground">
              <p className="font-medium text-foreground mb-1">About Cash Flow Alerts</p>
              <p>
                When enabled, we&apos;ll send you an email alert <strong>5 days before</strong> your 
                checking account is projected to go negative. Alerts are checked daily at noon EST.
              </p>
              <p className="mt-2">
                Alert status: <strong className={alertSettings.enabled ? "text-emerald-600" : "text-muted-foreground"}>
                  {alertSettings.enabled ? "Enabled" : "Disabled"}
                </strong>
              </p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

// Separate component to avoid complex nested expressions
const ShortfallItem = ({ shortfall, formatDate, formatCurrency }) => {
  const billsDue = shortfall.bills_due || [];
  
  return (
    <div className="flex items-center justify-between p-3 bg-rose-50 dark:bg-rose-950/30 rounded-lg">
      <div>
        <p className="font-medium text-rose-900 dark:text-rose-100">
          {formatDate(shortfall.date)}
        </p>
        <div className="flex flex-wrap gap-1 mt-1">
          {billsDue.map((bill, i) => (
            <Badge 
              key={i}
              variant="outline"
              className="text-xs border-rose-300 text-rose-700 dark:border-rose-700 dark:text-rose-300"
            >
              {bill.merchant}: {formatCurrency(bill.amount)}
            </Badge>
          ))}
        </div>
      </div>
      <div className="text-right">
        <p className="text-xs text-rose-600 dark:text-rose-400">Projected Balance</p>
        <p className="font-bold text-rose-700 dark:text-rose-300">
          {formatCurrency(shortfall.projected_balance)}
        </p>
      </div>
    </div>
  );
};
